import { z } from "zod";
import { ApiMoney } from "./tasks";

/**
 * Düzenli rezervasyon (K-118), favoriler (K-054) ve "Tekrar rezerve et" (K-046).
 */
export const SeriesFrequency = z.enum(["weekly", "biweekly", "monthly"]);

/** POST /v1/tasks/:id/series — müşteri, hizmet verenle yaptığı bir işten seri önerir */
export const CreateSeriesBody = z.object({
  frequency: SeriesFrequency,
  /** ilk işin başlangıcı (en az 1 saat, en çok 90 gün ileride) */
  firstStart: z.string().datetime({ offset: true }),
});
export type CreateSeriesBody = z.infer<typeof CreateSeriesBody>;

/** POST /v1/provider/series/:id/accept — şirkette işi yapacak kişi seçilir */
export const AcceptSeriesBody = z.object({ memberId: z.string().uuid().optional() });

/** POST /v1/provider/series/:id/rate — hizmet veren yeni saatlik fiyat önerir (komisyonsuz, kuruş) */
export const ProposeSeriesRateBody = z.object({
  hourlyRateMinor: z
    .string()
    .regex(/^\d{1,7}$/)
    .refine((v) => BigInt(v) > 0n, { message: "positive" }),
});

export const EndSeriesBody = z.object({ reason: z.string().trim().max(300).optional() });

export const SeriesView = z.object({
  id: z.string().uuid(),
  status: z.enum(["proposed", "active", "paused", "ended", "declined"]),
  frequency: SeriesFrequency,
  serviceName: z.string(),
  providerId: z.string().uuid(),
  providerName: z.string().nullable(),
  /** sıradaki işin başlangıcı */
  nextStart: z.string().nullable(),
  scheduledMinutes: z.number(),
  /** müşteri görünümünde Poji ücreti dahil, hizmet veren görünümünde komisyonsuz */
  hourlyPrice: ApiMoney,
  /** hizmet verenin önerdiği yeni fiyat (aynı görünüm kuralı), müşteri onayı bekliyor */
  proposedHourlyPrice: ApiMoney.nullable(),
  templateTaskId: z.string().uuid(),
  createdAt: z.string(),
  endedAt: z.string().nullable(),
  endReason: z.string().nullable(),
  /** seriden oluşmuş işler (en yeni 10) */
  tasks: z.array(
    z.object({ id: z.string().uuid(), status: z.string(), scheduledStart: z.string().nullable() }),
  ),
});
export type SeriesView = z.infer<typeof SeriesView>;

export const FavoriteView = z.object({
  providerId: z.string().uuid(),
  displayName: z.string().nullable(),
  slug: z.string().nullable(),
  /** kapak çizimi kodu, ör. cleaning:3 */
  coverArt: z.string().nullable(),
  /** yüklenen kapak fotoğrafının yolu (provider-media); varsa çizimin önüne geçer */
  coverImagePath: z.string().nullable(),
  /** ilk aktif hizmetin adı */
  service: z.string().nullable(),
  rating: z.object({ average: z.number().nullable(), count: z.number() }),
  completedTasks: z.number(),
  /** müşterinin kendine notu */
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type FavoriteView = z.infer<typeof FavoriteView>;

export const FavoriteNoteInput = z.object({
  /** boş metin notu siler */
  note: z.string().trim().max(500).nullable(),
});
export type FavoriteNoteInput = z.infer<typeof FavoriteNoteInput>;

/** POST /v1/tasks/:id/rebook cevabı: yeni taslak + aynı hizmet veren önerisi */
export const RebookView = z.object({
  id: z.string().uuid(),
  status: z.literal("draft"),
  estimatedMinutes: z.number().nullable(),
  rebookedFromTaskId: z.string().uuid(),
  suggestedProviderId: z.string().uuid().nullable(),
});
export type RebookView = z.infer<typeof RebookView>;
